import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, Search, Filter, Mail, Phone, Calendar,
  Eye, Edit, CheckCircle, XCircle, AlertTriangle,
  RefreshCw, Building, User, MapPin, ChevronRight, X,
  Shield, Percent,
  AlertCircle as AlertCircleIcon
} from 'lucide-react';
import api from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import {
  callAuthPostWithFallback,
  STAFF_MANAGEMENT_PRIMARY_URL,
  AUTH_LAMBDA_FUNCTION_URL,
  callStaffLambdaDirect
} from '../../utils/authFallback';

const initialEditFormState = {
  companyName: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  address: '',
  city: '',
  state: '',
  zipCode: '',
  country: '',
  status: 'active'
};

const normalizeDiscountRecord = (raw) => {
  if (!raw) return null;
  const discountType = raw.discountType || raw.discount_type || 'percent';
  const discountValueRaw = raw.discountValue ?? raw.discount_value;

  return {
    id: raw.id,
    plan: raw.plan || '',
    discountType,
    discountValue: typeof discountValueRaw === 'number' ? discountValueRaw : Number(discountValueRaw || 0),
    durationInMonths: raw.durationInMonths ?? raw.duration_in_months ?? null,
    startAt: raw.startAt || raw.start_at || null,
    endAt: raw.endAt || raw.end_at || null,
    couponId: raw.couponId || raw.coupon_id || null,
    status: raw.status || 'active'
  };
};

const formatDateForDisplay = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-GB', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const formatCurrency = (value) => {
  if (!Number.isFinite(value)) return '£0.00';
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP'
  }).format(value);
};

const MobileClientManagement = () => {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalClients, setTotalClients] = useState(0);
  const [clientsPerPage] = useState(20);
  
  // Modal states
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [viewingClient, setViewingClient] = useState(null);
  const [editingClient, setEditingClient] = useState(null);
  const [editForm, setEditForm] = useState(initialEditFormState);
  const [twoFactorInfo, setTwoFactorInfo] = useState(null);
  const [twoFactorLoading, setTwoFactorLoading] = useState(false);
  const [twoFactorError, setTwoFactorError] = useState(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetConfirmationEmail, setResetConfirmationEmail] = useState('');
  const [resettingTwoFactor, setResettingTwoFactor] = useState(false);
  const [discountInfo, setDiscountInfo] = useState(null);
  const [discountLoading, setDiscountLoading] = useState(false);
  const [discountError, setDiscountError] = useState(null);
  const [discountForm, setDiscountForm] = useState({
    type: 'percent',
    value: '',
    endDate: ''
  });
  const [savingDiscount, setSavingDiscount] = useState(false);
  const [removingDiscount, setRemovingDiscount] = useState(false);

  const normalizeStaffApiPath = (rawPath) => {
    const trimmed = typeof rawPath === 'string' ? rawPath.trim() : '';
    if (!trimmed) {
      return '/api/staff';
    }
    let withLeadingSlash = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
    if (!withLeadingSlash.startsWith('/api')) {
      withLeadingSlash = `/api${withLeadingSlash}`;
    }
    return withLeadingSlash;
  };

  const buildAuthProxyPath = (staffPath) => {
    const withoutApiPrefix = staffPath.startsWith('/api')
      ? staffPath.slice(4)
      : staffPath;
    return `/api/auth${withoutApiPrefix.startsWith('/') ? withoutApiPrefix : `/${withoutApiPrefix}`}`;
  };

  const callStaffManagement = async ({ path, method = 'get', payload = {} }) => {
    const staffPath = normalizeStaffApiPath(path);
    const methodUpper = String(method || 'get').toUpperCase();
    const isReadOnlyRequest = methodUpper === 'GET';
    const isSecurityPath = staffPath.includes('/staff/clients/');
    const fallbackBase = isSecurityPath
      ? AUTH_LAMBDA_FUNCTION_URL
      : STAFF_MANAGEMENT_PRIMARY_URL;
    const resolvedStaffId = user?.id || user?.userId || null;
    const staffHeaders = resolvedStaffId
      ? {
          'staff-id': resolvedStaffId,
          'user-id': resolvedStaffId
        }
      : {};

    const executeDirect = async () => {
      return callStaffLambdaDirect({
        path: staffPath,
        method,
        payload,
        additionalHeaders: staffHeaders
      });
    };

    if (isSecurityPath) {
      return callAuthPostWithFallback(api, {
        apiPath: staffPath,
        fallbackPath: staffPath,
        method,
        payload,
        authMode: 'query',
        requireAuthToken: true,
        allowFallbackOnProduction: true,
        forceFallback: true,
        fallbackBaseUrlOverride: fallbackBase
      });
    }

    if (isReadOnlyRequest) {
      try {
        return await executeDirect();
      } catch (directError) {
        console.warn('[MobileClientManagement] direct GET failed, using fallback', directError);
        return callAuthPostWithFallback(api, {
          apiPath: staffPath,
          fallbackPath: staffPath,
          method,
          payload,
          authMode: 'query',
          requireAuthToken: true,
          allowFallbackOnProduction: true,
          forceFallback: true,
          fallbackBaseUrlOverride: fallbackBase
        });
      }
    }

    try {
      return await executeDirect();
    } catch (directError) {
      console.warn('[MobileClientManagement] direct write failed, using fallback proxy', directError);
    }

    const proxyPath = buildAuthProxyPath(staffPath);

    return callAuthPostWithFallback(api, {
      apiPath: proxyPath,
      fallbackPath: staffPath,
      method,
      payload,
      authMode: 'query',
      requireAuthToken: true,
      allowFallbackOnProduction: true,
      forceFallback: true,
      fallbackBaseUrlOverride: fallbackBase
    });
  };

  useEffect(() => {
    fetchClients();
  }, [currentPage, statusFilter]);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        limit: clientsPerPage,
        search: searchQuery,
        status: statusFilter !== 'all' ? statusFilter : '',
        sortBy: 'created_at',
        sortOrder: 'DESC'
      });

      const response = await api.get(`/clients?${params}`);
      
      if (response.data.success && response.data.data) {
        const clientsArray = response.data.data.clients || response.data.data || [];
        setClients(clientsArray);
        setTotalClients(clientsArray.length);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchTwoFactorInfo = async (clientId) => {
    if (!clientId) {
      setTwoFactorInfo(null);
      return;
    }

    setTwoFactorLoading(true);
    setTwoFactorError(null);

    try {
      const response = await callStaffManagement({
        path: `/staff/clients/${clientId}/security`,
        method: 'get',
        payload: {},
      });
      if (response?.success) {
        setTwoFactorInfo(response.data || null);
      } else {
        setTwoFactorInfo(null);
        setTwoFactorError(response?.error || 'Unable to load two-factor details');
      }
    } catch (error) {
      console.error('Error fetching two-factor info:', error);
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Unable to load two-factor details';
      setTwoFactorError(message);
      setTwoFactorInfo(null);
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchClients();
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      case 'suspended': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <CheckCircle className="w-4 h-4" />;
      case 'inactive': return <XCircle className="w-4 h-4" />;
      case 'suspended': return <AlertTriangle className="w-4 h-4" />;
      default: return <XCircle className="w-4 h-4" />;
    }
  };

  const filteredClients = clients.filter(client => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      client.company_name?.toLowerCase().includes(query) ||
      client.contact_name?.toLowerCase().includes(query) ||
      client.email?.toLowerCase().includes(query) ||
      client.companyName?.toLowerCase().includes(query) ||
      (client.firstName + ' ' + client.lastName)?.toLowerCase().includes(query)
    );
  });

  const totalPages = Math.ceil(totalClients / clientsPerPage);

  // Handle view client
  const handleViewClient = (client) => {
    setViewingClient(client);
    setShowViewModal(true);
  };

  // Handle edit client
  const handleEditClient = (client) => {
    setEditingClient(client);
    setTwoFactorInfo(null);
    setTwoFactorError(null);
    setShowResetConfirm(false);
    setResetConfirmationEmail('');
    setEditForm({
      companyName: client.company_name || client.companyName || '',
      firstName: client.firstName || client.first_name || '',
      lastName: client.lastName || client.last_name || '',
      email: client.email || '',
      phone: client.phone || '',
      address: client.address || '',
      city: client.city || '',
      state: client.state || '',
      zipCode: client.zipCode || client.zip_code || '',
      country: client.country || '',
      status: client.status || 'active'
    });
    setShowEditModal(true);
    fetchTwoFactorInfo(client.id);
    fetchDiscountInfo(client.id);
  };

  // Handle save edited client
  const handleSaveEdit = async () => {
    try {
      setLoading(true);
      
      await api.put(`/api/clients/${editingClient.id}`, {
        firstName: editForm.firstName,
        lastName: editForm.lastName,
        companyName: editForm.companyName,
        phone: editForm.phone,
        address: editForm.address,
        city: editForm.city,
        state: editForm.state,
        zipCode: editForm.zipCode,
        country: editForm.country,
        status: editForm.status
      });
      
      await fetchClients();
      closeEditModal();
      alert('Client updated successfully!');
    } catch (error) {
      console.error('Error updating client:', error);
      alert('Failed to update client. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchDiscountInfo = async (clientId) => {
    if (!clientId) {
      setDiscountInfo(null);
      return;
    }

    setDiscountLoading(true);
    setDiscountError(null);

    try {
      const response = await callStaffManagement({
        path: `/api/staff/users/${clientId}/discount`,
        method: 'get',
        payload: {}
      });

      if (response?.success && response.discount) {
        const normalized = normalizeDiscountRecord(response.discount);
        setDiscountInfo(normalized);
        setDiscountForm({
          type: normalized.discountType || 'percent',
          value: normalized.discountValue ? String(normalized.discountValue) : '',
          endDate: normalized.endAt ? normalized.endAt.slice(0, 10) : ''
        });
      } else {
        setDiscountInfo(null);
        setDiscountForm({
          type: 'percent',
          value: '',
          endDate: ''
        });
      }
    } catch (error) {
      console.error('Error fetching discount info:', error);
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Unable to load discount information.';
      setDiscountInfo(null);
      setDiscountError(message);
    } finally {
      setDiscountLoading(false);
    }
  };

  const handleApplyDiscount = async () => {
    if (!editingClient) {
      alert('Select a client first.');
      return;
    }

    const plan = String(
      editingClient.subscription_plan ||
      editingClient.subscriptionPlan ||
      ''
    ).toLowerCase();

    if (!plan) {
      alert('Unable to determine the client’s current plan.');
      return;
    }

    const numericValue = Number(discountForm.value);
    if (!Number.isFinite(numericValue) || numericValue <= 0) {
      alert('Enter a discount value greater than zero.');
      return;
    }

    if (discountForm.type === 'percent' && (numericValue <= 0 || numericValue > 100)) {
      alert('Percentage discounts must be between 0 and 100.');
      return;
    }

    setSavingDiscount(true);
    setDiscountError(null);

    try {
      const response = await callStaffManagement({
        path: `/api/staff/users/${editingClient.id}/discount`,
        method: 'put',
        payload: {
          plan,
          discountType: discountForm.type,
          value: numericValue,
          endDate: discountForm.endDate || null
        }
      });

      if (response?.success) {
        alert('Discount applied successfully.');
        await fetchDiscountInfo(editingClient.id);
      } else {
        const message = response?.message || response?.error || 'Failed to apply discount.';
        setDiscountError(message);
        alert(message);
      }
    } catch (error) {
      console.error('Error applying discount:', error);
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to apply discount.';
      setDiscountError(message);
      alert(message);
    } finally {
      setSavingDiscount(false);
    }
  };

  const handleRemoveDiscount = async () => {
    if (!editingClient || !discountInfo) {
      return;
    }

    if (!window.confirm('Remove the active discount from this subscription?')) {
      return;
    }

    setRemovingDiscount(true);
    setDiscountError(null);

    try {
      const response = await callStaffManagement({
        path: `/api/staff/users/${editingClient.id}/discount`,
        method: 'delete',
        payload: {}
      });

      if (response?.success) {
        alert('Discount removed.');
        await fetchDiscountInfo(editingClient.id);
      } else {
        const message = response?.message || response?.error || 'Failed to remove discount.';
        setDiscountError(message);
        alert(message);
      }
    } catch (error) {
      console.error('Error removing discount:', error);
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to remove discount.';
      setDiscountError(message);
      alert(message);
    } finally {
      setRemovingDiscount(false);
    }
  };

  const handleConfirmTwoFactorReset = async () => {
    if (!editingClient) return;

    const expectedEmail = (editingClient.email || '').trim().toLowerCase();
    const providedEmail = resetConfirmationEmail.trim().toLowerCase();

    if (!expectedEmail || providedEmail !== expectedEmail) {
      setTwoFactorError('Email confirmation does not match the selected client.');
      return;
    }

    try {
      setResettingTwoFactor(true);
      setTwoFactorError(null);
      await callStaffManagement({
        path: `/staff/clients/${editingClient.id}/two-factor/reset`,
        method: 'post',
        payload: {},
      });
      alert('Two-factor authentication has been reset. The client will need to reconfigure at next login.');
      setShowResetConfirm(false);
      setResetConfirmationEmail('');
      await fetchTwoFactorInfo(editingClient.id);
    } catch (error) {
      console.error('Error resetting two-factor authentication:', error);
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to reset two-factor authentication. Please try again.';
      setTwoFactorError(message);
      alert(message);
    } finally {
      setResettingTwoFactor(false);
    }
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingClient(null);
    setEditForm(initialEditFormState);
    setTwoFactorInfo(null);
    setTwoFactorError(null);
    setShowResetConfirm(false);
    setResetConfirmationEmail('');
    setTwoFactorLoading(false);
    setResettingTwoFactor(false);
    setDiscountInfo(null);
    setDiscountError(null);
    setDiscountLoading(false);
    setDiscountForm({
      type: 'percent',
      value: '',
      endDate: ''
    });
  };

  const getTwoFactorStatusDetails = () => {
    if (!twoFactorInfo) {
      return {
        label: 'Unknown',
        badgeClass: 'bg-gray-100 text-gray-600'
      };
    }

    switch (twoFactorInfo.status) {
      case 'enabled':
        return { label: 'Enabled', badgeClass: 'bg-green-100 text-green-700' };
      case 'reconfiguration_required':
        return { label: 'Reconfiguration required', badgeClass: 'bg-amber-100 text-amber-700' };
      default:
        return { label: 'Not configured', badgeClass: 'bg-gray-100 text-gray-600' };
    }
  };

  // Handle delete client
  const handleDeleteClient = async (client) => {
    if (!confirm(`Are you sure you want to delete ${client.company_name || client.companyName || 'this client'}? This action cannot be undone.`)) {
      return;
    }

    try {
      setLoading(true);
      await api.delete(`/api/clients/${client.id}`);
      await fetchClients();
      alert('Client deleted successfully!');
    } catch (error) {
      console.error('Error deleting client:', error);
      alert('Failed to delete client. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-white sticky top-0 z-10 shadow-sm">
        {/* Title */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
          <div>
              <h1 className="text-xl font-bold text-gray-900">Client Management</h1>
              <p className="text-sm text-gray-600 mt-1">Manage client accounts</p>
          </div>
          </div>
        </div>

        {/* Search and Filter Bar */}
        <div className="p-4 space-y-3">
          <div className="flex items-center space-x-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search clients..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED] focus:border-transparent"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`p-2 rounded-lg border transition-colors ${
                showFilters || statusFilter !== 'all'
                  ? 'bg-[#1F11ED] text-white border-[#1F11ED]'
                  : 'bg-white text-gray-600 border-gray-300'
              }`}
            >
              <Filter className="w-5 h-5" />
            </button>
            <button
              onClick={fetchClients}
              className="p-2 rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
        </div>

          {/* Filters */}
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="space-y-3"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
          <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED] focus:border-transparent"
                >
                  <option value="all">All Statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="suspended">Suspended</option>
          </select>
              </div>
          <button 
                onClick={() => {
                  setStatusFilter('all');
                  setSearchQuery('');
                  setShowFilters(false);
                }}
                className="w-full text-sm text-gray-600 py-2"
              >
                Clear Filters
          </button>
      </motion.div>
          )}
        </div>
            </div>

      {/* Client List */}
      <div className="p-4 space-y-3">
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-40 bg-gray-200 rounded-xl animate-pulse"></div>
            ))}
          </div>
        ) : filteredClients.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No clients found</h3>
            <p className="text-sm text-gray-600">
              {searchQuery || statusFilter !== 'all'
                ? 'Try adjusting your search or filters'
                : 'No clients yet'}
            </p>
          </div>
        ) : (
          <AnimatePresence>
            {filteredClients.map((client, index) => (
            <motion.div
              key={client.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
              >
                {/* Card Header */}
                <div className="p-4">
                  <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <Building className="w-4 h-4 text-gray-400" />
                        <h3 className="font-semibold text-gray-900 text-base">
                          {client.company_name || client.companyName || `${client.firstName} ${client.lastName}`}
                  </h3>
                </div>
                      <p className="text-sm text-gray-600 flex items-center space-x-1">
                        <Mail className="w-3 h-3" />
                        <span>{client.email}</span>
                      </p>
                </div>
                    <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(client.status || 'active')}`}>
                      {getStatusIcon(client.status || 'active')}
                      <span>{(client.status || 'active').toUpperCase()}</span>
                    </span>
              </div>

              {/* Client Details */}
                  <div className="space-y-2 text-sm mb-4">
                {client.phone && (
                      <div className="flex items-center space-x-2 text-gray-600">
                        <Phone className="w-4 h-4" />
                        <span>{client.phone}</span>
                      </div>
                    )}
                    {(client.address || client.city) && (
                      <div className="flex items-center space-x-2 text-gray-600">
                        <MapPin className="w-4 h-4" />
                        <span>
                          {[client.address, client.city, client.country].filter(Boolean).join(', ')}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center space-x-2 text-gray-600">
                      <Calendar className="w-4 h-4" />
                      <span>Joined: {formatDate(client.created_at || client.createdAt)}</span>
                    </div>
                    {(client.last_login || client.lastLogin) && (
                      <div className="flex items-center space-x-2 text-gray-600">
                        <User className="w-4 h-4" />
                        <span>Last login: {formatDate(client.last_login || client.lastLogin)}</span>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleViewClient(client)}
                      className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                    >
                      <Eye className="w-4 h-4" />
                      <span className="text-sm font-medium">View</span>
                    </button>
                    <button
                      onClick={() => handleEditClient(client)}
                      className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                    >
                      <Edit className="w-4 h-4" />
                      <span className="text-sm font-medium">Edit</span>
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-4 pb-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className={`px-4 py-2 rounded-lg font-medium ${
                  currentPage === 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-[#1F11ED] text-white hover:bg-blue-700'
                }`}
              >
                Previous
              </button>
              
              <span className="text-sm text-gray-600">
                Page {currentPage} of {totalPages}
              </span>
              
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className={`px-4 py-2 rounded-lg font-medium ${
                  currentPage === totalPages
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-[#1F11ED] text-white hover:bg-blue-700'
                }`}
              >
                Next
              </button>
            </div>
            <div className="text-center mt-2 text-xs text-gray-500">
              {totalClients} total clients
            </div>
          </div>
                  </div>
                )}
                
      {/* View Client Modal */}
      {showViewModal && viewingClient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
              <h2 className="text-lg font-bold text-gray-900">Client Details</h2>
              <button
                onClick={() => setShowViewModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              <div>
                <label className="text-xs text-gray-500 uppercase">Company Name</label>
                <p className="text-base font-medium text-gray-900">{viewingClient.company_name || viewingClient.companyName || 'N/A'}</p>
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase">Contact Name</label>
                <p className="text-base font-medium text-gray-900">
                  {viewingClient.firstName || viewingClient.first_name} {viewingClient.lastName || viewingClient.last_name}
                </p>
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase">Email</label>
                <p className="text-base font-medium text-gray-900">{viewingClient.email}</p>
              </div>
              {viewingClient.phone && (
                <div>
                  <label className="text-xs text-gray-500 uppercase">Phone</label>
                  <p className="text-base font-medium text-gray-900">{viewingClient.phone}</p>
                </div>
              )}
              {(viewingClient.address || viewingClient.city) && (
                <div>
                  <label className="text-xs text-gray-500 uppercase">Address</label>
                  <p className="text-base font-medium text-gray-900">
                    {[viewingClient.address, viewingClient.city, viewingClient.state, viewingClient.zipCode || viewingClient.zip_code, viewingClient.country].filter(Boolean).join(', ') || 'N/A'}
                  </p>
                </div>
              )}
              <div>
                <label className="text-xs text-gray-500 uppercase">Status</label>
                <div className="mt-1">
                  <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(viewingClient.status || 'active')}`}>
                    {getStatusIcon(viewingClient.status || 'active')}
                    <span>{(viewingClient.status || 'active').toUpperCase()}</span>
                  </span>
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase">Joined Date</label>
                <p className="text-base font-medium text-gray-900">{formatDate(viewingClient.created_at || viewingClient.createdAt)}</p>
              </div>
              {(viewingClient.last_login || viewingClient.lastLogin) && (
                <div>
                  <label className="text-xs text-gray-500 uppercase">Last Login</label>
                  <p className="text-base font-medium text-gray-900">{formatDate(viewingClient.last_login || viewingClient.lastLogin)}</p>
                  </div>
                )}
              </div>

            <div className="p-4 border-t border-gray-200 flex space-x-2">
              <button
                onClick={() => {
                  setShowViewModal(false);
                  handleEditClient(viewingClient);
                }}
                className="flex-1 bg-[#1F11ED] text-white py-3 rounded-lg hover:bg-blue-700 font-medium"
              >
                Edit Client
              </button>
              <button
                onClick={() => setShowViewModal(false)}
                className="px-6 bg-gray-100 text-gray-700 py-3 rounded-lg hover:bg-gray-200 font-medium"
              >
                Close
                </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Edit Client Modal */}
      {showEditModal && editingClient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
              <h2 className="text-lg font-bold text-gray-900">Edit Client</h2>
              <button
                onClick={closeEditModal}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
                </button>
            </div>
            
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                <input
                  type="text"
                  value={editForm.companyName}
                  onChange={(e) => setEditForm({ ...editForm, companyName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED] focus:border-transparent"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                  <input
                    type="text"
                    value={editForm.firstName}
                    onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED] focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                  <input
                    type="text"
                    value={editForm.lastName}
                    onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED] focus:border-transparent"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED] focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  type="tel"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED] focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <input
                  type="text"
                  value={editForm.address}
                  onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED] focus:border-transparent"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                  <input
                    type="text"
                    value={editForm.city}
                    onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED] focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                  <input
                    type="text"
                    value={editForm.state}
                    onChange={(e) => setEditForm({ ...editForm, state: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED] focus:border-transparent"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Zip Code</label>
                  <input
                    type="text"
                    value={editForm.zipCode}
                    onChange={(e) => setEditForm({ ...editForm, zipCode: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED] focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                  <input
                    type="text"
                    value={editForm.country}
                    onChange={(e) => setEditForm({ ...editForm, country: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED] focus:border-transparent"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={editForm.status}
                  onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED] focus:border-transparent"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>

              <div className="border border-gray-200 rounded-lg bg-gray-50 p-3">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 flex items-center">
                      <Shield className="w-4 h-4 mr-2 text-[#1F11ED]" />
                      Security &amp; Two-Factor
                    </h4>
                    <p className="text-xs text-gray-600 mt-1">
                      Reset two-factor if the client is unable to log in.
                    </p>
                  </div>
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTwoFactorStatusDetails().badgeClass}`}
                  >
                    {getTwoFactorStatusDetails().label}
                  </span>
                </div>

                <div className="mt-3 space-y-3 text-sm">
                  {twoFactorLoading ? (
                    <div className="flex items-center text-gray-500">
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Loading security details...
                    </div>
                  ) : twoFactorError ? (
                    <div className="rounded-md border border-red-200 bg-red-50 p-3 text-red-700 flex items-start">
                      <AlertCircleIcon className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium">Unable to load two-factor data</p>
                        <p>{twoFactorError}</p>
                      </div>
                    </div>
                  ) : twoFactorInfo ? (
                    <>
                      <div className="space-y-2 text-gray-700">
                        <div className="flex justify-between">
                          <span className="text-xs uppercase text-gray-500">First verified</span>
                          <span>{twoFactorInfo.firstVerifiedAt ? new Date(twoFactorInfo.firstVerifiedAt).toLocaleString() : 'Not verified'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-xs uppercase text-gray-500">Last verified</span>
                          <span>{twoFactorInfo.verifiedAt ? new Date(twoFactorInfo.verifiedAt).toLocaleString() : 'Not recorded'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-xs uppercase text-gray-500">Grace ends</span>
                          <span>{twoFactorInfo.gracePeriodEndsAt ? new Date(twoFactorInfo.gracePeriodEndsAt).toLocaleDateString() : 'N/A'}</span>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => {
                          setShowResetConfirm(true);
                          setResetConfirmationEmail('');
                          setTwoFactorError(null);
                        }}
                        className="w-full mt-2 inline-flex items-center justify-center rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                      >
                        Reset Two-Factor
                      </button>
                    </>
                  ) : (
                    <div className="text-gray-500">
                      Two-factor details are not available.
                    </div>
                  )}

                  {showResetConfirm && (
                    <div className="border border-red-200 bg-red-50 rounded-md p-3 text-sm">
                      <p className="font-medium text-red-700">
                        Confirm reset for {editingClient.email}
                      </p>
                      <p className="text-xs text-red-600 mt-1">
                        This will invalidate their existing authenticator. They must set it up again.
                      </p>
                      <label className="block text-xs text-gray-700 mt-3 mb-1">
                        Type the client&rsquo;s email to confirm
                      </label>
                      <input
                        type="email"
                        value={resetConfirmationEmail}
                        onChange={(e) => setResetConfirmationEmail(e.target.value)}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-red-500 focus:ring-2 focus:ring-red-500/50"
                        placeholder={editingClient.email}
                      />
                      <div className="mt-3 flex items-center gap-2">
                        <button
                          type="button"
                          onClick={handleConfirmTwoFactorReset}
                          disabled={
                            resettingTwoFactor ||
                            resetConfirmationEmail.trim().toLowerCase() !== (editingClient.email || '').trim().toLowerCase()
                          }
                          className={`flex-1 inline-flex items-center justify-center rounded-md px-3 py-2 text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                            resettingTwoFactor ||
                            resetConfirmationEmail.trim().toLowerCase() !== (editingClient.email || '').trim().toLowerCase()
                              ? 'bg-red-300 cursor-not-allowed'
                              : 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                          }`}
                        >
                          {resettingTwoFactor ? 'Resetting…' : 'Confirm Reset'}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowResetConfirm(false);
                            setResetConfirmationEmail('');
                            setTwoFactorError(null);
                          }}
                          className="px-3 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
              <div className="border border-gray-200 rounded-lg bg-gray-50 p-3">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 flex items-center">
                      <Percent className="w-4 h-4 mr-2 text-[#1F11ED]" />
                      Subscription discount
                    </h4>
                    <p className="text-xs text-gray-600 mt-1">
                      Discounts apply from the client’s next invoice. Only one discount can be active at a time.
                    </p>
                  </div>
                </div>

                <div className="mt-3 space-y-3 text-sm">
                  {discountLoading ? (
                    <div className="flex items-center text-gray-500">
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Loading discount details...
                    </div>
                  ) : discountError ? (
                    <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-800">
                      <p className="font-medium">Unable to load discount info</p>
                      <p>{discountError}</p>
                    </div>
                  ) : discountInfo ? (
                    <div className="rounded-lg border border-green-200 bg-white p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="font-semibold text-gray-900">Current discount</p>
                        <span className="text-xs uppercase text-gray-500">
                          {discountInfo.status === 'active' ? 'Active' : discountInfo.status}
                        </span>
                      </div>
                      <ul className="text-sm text-gray-700 space-y-1">
                        <li>
                          {discountInfo.discountType === 'percent'
                            ? `${discountInfo.discountValue}% off`
                            : `${formatCurrency(discountInfo.discountValue)} off`}
                        </li>
                        <li>Starts: {formatDateForDisplay(discountInfo.startAt) || 'Next invoice'}</li>
                        <li>
                          Ends:{' '}
                          {discountInfo.endAt
                            ? formatDateForDisplay(discountInfo.endAt)
                            : discountInfo.durationInMonths
                              ? `${discountInfo.durationInMonths} billing cycle${discountInfo.durationInMonths > 1 ? 's' : ''}`
                              : 'Until removed'}
                        </li>
                        {discountInfo.couponId && (
                          <li className="text-xs text-gray-500">Stripe coupon: {discountInfo.couponId}</li>
                        )}
                      </ul>
                    </div>
                  ) : (
                    <div className="text-gray-500 text-sm">
                      No active discount. Use the form below to add one.
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                      <select
                        value={discountForm.type}
                        onChange={(e) => setDiscountForm({ ...discountForm, type: e.target.value })}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-[#1F11ED]"
                      >
                        <option value="percent">Percent off</option>
                        <option value="amount">Amount off</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        {discountForm.type === 'percent' ? 'Percent (0-100)' : 'Amount (£)'}
                      </label>
                      <input
                        type="number"
                        min="0"
                        max={discountForm.type === 'percent' ? '100' : undefined}
                        step={discountForm.type === 'percent' ? '1' : '0.01'}
                        value={discountForm.value}
                        onChange={(e) => setDiscountForm({ ...discountForm, value: e.target.value })}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-[#1F11ED]"
                        placeholder={discountForm.type === 'percent' ? 'e.g. 10' : 'e.g. 15.00'}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">End date (optional)</label>
                    <input
                      type="date"
                      value={discountForm.endDate}
                      onChange={(e) => setDiscountForm({ ...discountForm, endDate: e.target.value })}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-[#1F11ED]"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Leave blank for an indefinite discount. When set, the discount ends after that billing cycle.
                    </p>
                  </div>

                  <div className="flex flex-col space-y-2">
                    <button
                      type="button"
                      onClick={handleApplyDiscount}
                      disabled={savingDiscount}
                      className="w-full inline-flex justify-center px-4 py-2 rounded-lg bg-[#1F11ED] text-white font-medium disabled:opacity-50"
                    >
                      {savingDiscount ? 'Saving...' : 'Save discount'}
                    </button>
                    {discountInfo && (
                      <button
                        type="button"
                        onClick={handleRemoveDiscount}
                        disabled={removingDiscount}
                        className="w-full inline-flex justify-center px-4 py-2 rounded-lg border border-red-200 bg-red-50 text-red-700 font-medium disabled:opacity-50"
                      >
                        {removingDiscount ? 'Removing...' : 'Remove discount'}
                      </button>
                    )}
                  </div>
                </div>
              </div>

            <div className="p-4 border-t border-gray-200 flex space-x-2">
              <button
                onClick={handleSaveEdit}
                disabled={loading}
                className="flex-1 bg-[#1F11ED] text-white py-3 rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50"
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                onClick={closeEditModal}
                className="px-6 bg-gray-100 text-gray-700 py-3 rounded-lg hover:bg-gray-200 font-medium"
              >
                Cancel
                </button>
              </div>
            </motion.div>
        </div>
        )}
    </div>
  );
};

export default MobileClientManagement;
